import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableContainer,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import RoomPreferencesIcon from "@mui/icons-material/RoomPreferences";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShareIcon from "@mui/icons-material/Share";
import EmptyPagesIcon from "../../assets/iconComponents/EmptyPagesIcon";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useNotification } from "../../context/NotificationContext";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import PageService from "../../services/PageService";
import ProjectService from "../../services/ProjectService";
import ScanService from "../../services/ScanService";
import StatsSummary from "../StatsSummary";
import ViewDropdown from "./ViewDropdown";
import SearchBar from "./SearchBar";
import ActionButtons from "./ActionButtons";
import PagesFilters from "./PagesFilters";
import DeletePagesConfirmationModal from "../modals/DeletePagesConfirmationModal";
import CreatePresetModal from "../modals/CreatePresetModal";
import EditPresetModal from "../modals/EditPresetModal";
import GenerateTestCasesConfirmationModal from "../modals/GenerateTestCasesConfirmationModal";
import GenerateTestSettingsModal from "../modals/GenerateTestSettingsModal";
import TableHeader from "./views/table/TableHeader";
import TableContent from "./views/table/TableContent";
import PaginationComponent from "../PaginationComponent";
import HierarchicalTableHeader from "./views/hierarchical/HierarchicalTableHeader";
import HierarchicalTableContent from "./views/hierarchical/HierarchicalTableContent";
import { sortTableItems } from "../../utils/sortTableItems";

const PagesContent = ({
  projectID,
  pagesList,
  prevPagesList,
  sitemap,
  prevSitemap,
  handleAddPageOpen,
  pagesToggler,
  setPagesToggler,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const showNotification = useNotification();
  const { user } = useAuth();
  const { selectedProject } = useProject();

  const [view, setView] = useState("table");
  const [projectData, setProjectData] = useState(() =>
    selectedProject?.id === projectID ? selectedProject : null,
  );
  const [checkedPages, setCheckedPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [createPresetOpen, setCreatePresetOpen] = useState(false);
  const [editPresetOpen, setEditPresetOpen] = useState(false);
  const [
    generateTestCasesForAllPagesOpen,
    setGenerateTestCasesForAllPagesOpen,
  ] = useState(false);
  const [generateTestSettingsModalOpen, setGenerateTestSettingsModalOpen] =
    useState(false);
  const [pendingPageIds, setPendingPageIds] = useState([]);
  const [presetsList, setPresetsList] = useState([]);
  const [presetToEdit, setPresetToEdit] = useState({ id: "", name: "" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pagesToDelete, setPagesToDelete] = useState([]);
  const [page, setPage] = useState(1);
  const [projectStat, setProjectStat] = useState(null);
  const [filteredPages, setFilteredPages] = useState(pagesList);
  const [allIssueTypes, setAllIssueTypes] = useState([]);
  const [selectedIssueTypes, setSelectedIssueTypes] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "testCasesTotal",
    direction: "desc",
  });
  const [selectedScanStatus, setSelectedScanStatus] = useState("All");
  const [selectedTestCasesStatus, setSelectedTestCasesStatus] = useState("All");
  const [loginRequired, setLoginRequired] = useState(false);

  const open = Boolean(anchorEl);
  const itemsPerPage = 10;

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  useEffect(() => {
    if (pagesList) {
      const newFilteredPages = pagesList.filter((page) => {
        const matchesSearch =
          page.label?.toLowerCase()?.includes(selectedPage.toLowerCase()) ||
          page.url.toLowerCase().includes(selectedPage.toLowerCase());

        const matchesScanStatus =
          selectedScanStatus === "All" ||
          (selectedScanStatus === "Scanned" && page.last_scanned_at) ||
          (selectedScanStatus === "Unscanned" && !page.last_scanned_at);

        const matchesTestCasesStatus =
          selectedTestCasesStatus === "All" ||
          (selectedTestCasesStatus === "Generated" && page.testCasesTotal) ||
          (selectedTestCasesStatus === "Not Generated" && !page.testCasesTotal);

        return matchesSearch && matchesScanStatus && matchesTestCasesStatus;
      });

      setFilteredPages(newFilteredPages);
    }
  }, [
    selectedPage,
    selectedScanStatus,
    selectedTestCasesStatus,
    pagesList,
    projectID,
  ]);

  useEffect(() => {
    if (projectID) {
      const getProjectData = async () => {
        try {
          const data = await ProjectService.getProjectById(projectID);
          setProjectData((prev) =>
            prev?.id === data?.id ? { ...prev, ...data } : data,
          );
          setLoginRequired(data?.login_required);
          setProjectStat(data.stat);
        } catch (error) {
          console.error("Failed to fetch project data:", error);
        }
      };

      getProjectData();
    }
  }, [projectID]);

  useEffect(() => {
    if (!projectID) {
      setProjectData(null);
      return;
    }

    if (selectedProject?.id === projectID) {
      setProjectData((prev) =>
        prev?.id === projectID
          ? { ...selectedProject, ...prev }
          : selectedProject,
      );
      return;
    }

    setProjectData((prev) => (prev?.id === projectID ? prev : null));
  }, [projectID, selectedProject]);

  useEffect(() => {
    const getAllIssueTypesData = async () => {
      const allIssueTypesData = await ProjectService.getIssueTypes();
      setAllIssueTypes(allIssueTypesData);
      setSelectedIssueTypes(allIssueTypesData.map((type) => type.description));
    };

    getAllIssueTypesData();
  }, []);

  useEffect(() => {
    const getAllPresetsData = async () => {
      const allPresetsData =
        await PageService.getAllPresetsByProjectId(projectID);
      setPresetsList(allPresetsData);
    };

    getAllPresetsData();
  }, [projectID, pagesToggler]);

  useEffect(() => {
    if (location.state?.view) {
      setView(location.state.view);
    }
  }, [location.state]);

  const sortedPages = sortTableItems(filteredPages, sortConfig);

  let paginatedPages;
  const selectedIssueTypesIds = allIssueTypes
    .filter((type) => selectedIssueTypes.includes(type.description))
    .map((type) => type.id);
  const filteredPagesByIssues = sortedPages.filter((page) =>
    page?.issue_classes?.some((issueClass) =>
      selectedIssueTypesIds.includes(issueClass),
    ),
  );

  if (allIssueTypes.length === selectedIssueTypes.length) {
    paginatedPages = sortedPages.slice(startIndex, endIndex);
  } else {
    paginatedPages = filteredPagesByIssues.slice(startIndex, endIndex);
  }

  const totalItems =
    allIssueTypes.length === selectedIssueTypes.length
      ? filteredPages.length
      : filteredPagesByIssues.length;
  const totalPages =
    allIssueTypes.length === selectedIssueTypes.length
      ? Math.ceil(filteredPages.length / itemsPerPage)
      : Math.ceil(filteredPagesByIssues.length / itemsPerPage);

  const handleSortChange = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleSearch = (event, newValue) => {
    setSelectedPage(newValue?.label || "");
    setPage(1);
  };

  const handleDiscoverMore = async () => {
    try {
      showNotification(
        "Page Tree Scan has started",
        "info",
        "Learn more",
        navigate,
      );
      await ProjectService.discoverMorePage(projectID, checkedPages);
      setCheckedPages([]);
      setIsHeaderCheckboxChecked(false);
      setPagesToggler(!pagesToggler);
    } catch (error) {
      console.error("Error discovering more", error);
    }
  };

  const handleScanPages = async (pageIds) => {
    showNotification("UI Scan has started", "info", "Learn more", navigate);

    try {
      if (pageIds.length) {
        await ScanService.scanPages({ pageIds });
      } else {
        const allPageIds = [];

        pagesList.forEach((page) =>
          page.page_ids.forEach((id) => allPageIds.push(id)),
        );

        await ScanService.scanPages({ pageIds: allPageIds });
      }

      setCheckedPages([]);
    } catch (error) {
      console.log(error);
      if (error?.response?.data?.error === "Please check your auth data.") {
        showNotification(
          "Failed to scan. Please check your auth data",
          "error",
          "Learn more",
          navigate,
          `/project/${projectID}`,
          { state: { activeTab: "2" } },
        );
      }
    }
  };

  const handleGenerateTestCases = (pageIds) => {
    setPendingPageIds(pageIds);
    setGenerateTestSettingsModalOpen(true);
  };

  const handleRunGenerateTest = async (context) => {
    showNotification(
      "Test cases discovery has started",
      "info",
      "Learn more",
      navigate,
    );

    try {
      let targetPageIds = pendingPageIds;

      if (!targetPageIds.length) {
        const allPageIds = [];
        pagesList.forEach((page) =>
          page.page_ids.forEach((id) => allPageIds.push(id)),
        );
        targetPageIds = allPageIds;
      }

      // For multiple pages, apply context to each page individually
      // since discoverTestCasesMultiple doesn't support context
      if (targetPageIds.length === 1) {
        await PageService.discoverTestCasesByPageId(targetPageIds[0], context);
      } else {
        // For multiple pages, call the service for each page with context
        await Promise.all(
          targetPageIds.map((pageId) =>
            PageService.discoverTestCasesByPageId(pageId, context),
          ),
        );
      }

      setCheckedPages([]);
      setPendingPageIds([]);
    } catch (error) {
      console.log(error);
      setPendingPageIds([]);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleCheckboxChange = (id) => {
    setCheckedPages((prevCheckedPages) => {
      if (prevCheckedPages.includes(id)) {
        return prevCheckedPages.filter((pageId) => pageId !== id);
      } else {
        return [...prevCheckedPages, id];
      }
    });
  };

  const handleClickCheckAllDropdown = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseCheckAllDropdown = () => {
    setAnchorEl(null);
  };

  const handleCheckAllPages = () => {
    const currentPageIds = paginatedPages.map((page) => page.id);
    setCheckedPages((prevCheckedPages) => {
      return [...new Set([...prevCheckedPages, ...currentPageIds])];
    });
    setIsHeaderCheckboxChecked(true);
    handleCloseCheckAllDropdown();
  };

  const handleUncheckAllPages = () => {
    const currentPageIds = paginatedPages.map((page) => page.id);
    setCheckedPages((prevCheckedPages) => {
      return prevCheckedPages.filter((id) => !currentPageIds.includes(id));
    });
    setIsHeaderCheckboxChecked(false);
    handleCloseCheckAllDropdown();
  };

  const handleOpenProjectSettings = () => {
    navigate(`/project/${projectID}`);
  };

  const handleOpenProjectLoginSettings = () => {
    navigate(`/project/${projectID}`, { state: { activeTab: "2" } });
  };

  const handleOpenProjectShareSettings = () => {
    navigate(`/project/${projectID}`, { state: { activeTab: "4" } });
  };

  const handleOpenDeleteDialog = (pageId) => {
    if (pageId) {
      setPagesToDelete([pageId]);
    } else {
      setPagesToDelete(checkedPages);
    }
    setDeleteOpen(true);
  };

  const handleCreatePresetOpen = () => {
    setCreatePresetOpen(true);
  };

  const handleEditPresetOpen = (preset) => {
    setPresetToEdit(preset);
    setEditPresetOpen(true);
  };

  const handleFiltersOpen = () => {
    setFiltersOpen(!filtersOpen);
  };

  // Check if this is demo project
  const isDemoProject =
    projectData?.is_demo === true ||
    (selectedProject?.id === projectID && selectedProject?.is_demo === true);
  const canShareProject = Boolean(projectData?.is_owner);
  const hasAcmeCredentials =
    !!user?.acme_user_email && !!user?.acme_user_password;

  return (
    <Box className="pagesContentContainer rootWrapper">
      <Box className="pagesTopSection">
        <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography className="pagesTitle">Pages</Typography>
          {isDemoProject && hasAcmeCredentials && (
            <>
              <Typography
                variant="body2"
                style={{ color: "#666", marginLeft: "4px" }}
              >
                Your credentials for demo
              </Typography>
              <Tooltip
                PopperProps={{
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      backgroundColor: "rgb(229, 246, 253)",
                      color: "primary.main",
                    },
                  },
                }}
                title={
                  <Box style={{ padding: "4px" }}>
                    <Typography variant="body2" style={{ marginBottom: "8px" }}>
                      <strong>URL:</strong>{" "}
                      {projectData?.login_page_url ||
                        projectData?.url ||
                        "https://acme-saas-demo.web.app/"}
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: "4px" }}>
                      <strong>Email:</strong> {user.acme_user_email}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Password:</strong> {user.acme_user_password}
                    </Typography>
                  </Box>
                }
                arrow
                placement="right"
              >
                <InfoOutlinedIcon
                  style={{
                    fontSize: "20px",
                    color: "#1976d2",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            </>
          )}
        </Box>
        <Box className="pagesActions">
          {paginatedPages.length ? (
            <ActionButtons
              presetsList={presetsList}
              checkedPages={checkedPages}
              handleScanPages={handleScanPages}
              handleGenerateTestCases={handleGenerateTestCases}
              handleAddPageOpen={handleAddPageOpen}
              handleCreatePresetOpen={handleCreatePresetOpen}
              handleEditPresetOpen={handleEditPresetOpen}
              setGenerateTestCasesForAllPagesOpen={
                setGenerateTestCasesForAllPagesOpen
              }
            />
          ) : null}
          <Box className="pagesActionsSettings">
            <Tooltip
              title="To change resolution and other scanning parameters, go to General Settings."
              PopperProps={{
                sx: {
                  "& .MuiTooltip-tooltip": {
                    backgroundColor: "primary.main",
                  },
                },
              }}
            >
              <IconButton
                className="pagesActionsIconButton"
                onClick={handleOpenProjectSettings}
              >
                <SettingsOutlinedIcon color="secondary" />
              </IconButton>
            </Tooltip>
            {loginRequired ? (
              <Tooltip
                title="To change login settings, go to Login Settings."
                PopperProps={{
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      backgroundColor: "primary.main",
                    },
                  },
                }}
              >
                <IconButton
                  className="pagesActionsIconButton"
                  onClick={handleOpenProjectLoginSettings}
                >
                  <RoomPreferencesIcon color="secondary" />
                </IconButton>
              </Tooltip>
            ) : null}
            {canShareProject ? (
              <Tooltip
                title="Share project"
                PopperProps={{
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      backgroundColor: "primary.main",
                    },
                  },
                }}
              >
                <IconButton
                  className="pagesActionsIconButton"
                  onClick={handleOpenProjectShareSettings}
                >
                  <ShareIcon color="secondary" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </Box>
      </Box>
      <Box className="pagesStatsSection">
        <StatsSummary
          title="Total Pages"
          value={projectStat?.total_pages}
          color="primary"
        />
        <StatsSummary
          title="Pages Scanned"
          value={projectStat?.pages_scanned}
          color="primary"
        />
        <StatsSummary
          title="Pages with Issues"
          value={projectStat?.pages_with_issues}
          color="primary"
        />
        <StatsSummary
          title="Scanning Progress"
          value={
            projectStat?.scanning_progress
              ? `${projectStat?.scanning_progress} %`
              : null
          }
          color={
            Number(projectStat?.scanning_progress) <= 50
              ? "#d32f2f"
              : Number(projectStat?.scanning_progress) <= 80
                ? "#ebbf05"
                : "#198754"
          }
          tooltipText="Percentage of pages scanned for UI issues."
        />
        <StatsSummary
          title="Issue Rate"
          value={
            projectStat?.issue_rate ? `${projectStat?.issue_rate} %` : null
          }
          color={
            Number(projectStat?.issue_rate) <= 20
              ? "#198754"
              : Number(projectStat?.issue_rate) <= 80
                ? "#ebbf05"
                : "#d32f2f"
          }
          tooltipText={
            <>
              Percent of <b style={{ color: "#fff" }}>scanned</b> pages with
              issues.
            </>
          }
        />
      </Box>
      <>
        <Box className="pagesListSettingsContainer">
          <Box className="pagesListItemSettings">
            {checkedPages.length ? (
              <>
                <Button
                  id="deletePagesButton"
                  className="deleteButton"
                  variant="outlined"
                  color="error"
                  onClick={() => handleOpenDeleteDialog()}
                >
                  <DeleteOutlineOutlinedIcon
                    className="deleteButtonIcon"
                    color="error"
                  />
                  DELETE
                </Button>
                <Button
                  id="discoverMoreButton"
                  className="discoverMoreButton"
                  variant="outlined"
                  color="secondary"
                  onClick={handleDiscoverMore}
                >
                  DISCOVER MORE
                </Button>
              </>
            ) : null}
          </Box>
          <Box className="pagesListCommonSettings">
            {paginatedPages.length ? (
              <ViewDropdown view={view} setView={setView} />
            ) : null}
            {view === "table" ? (
              <>
                {pagesList.length ? (
                  <Box className="pagesListFilters">
                    <Button
                      className="pagesListFiltersTitleContainer"
                      variant="text"
                      color="secondary"
                      onClick={handleFiltersOpen}
                    >
                      <FilterListIcon
                        className="pagesListFiltersTitleIcon"
                        color="secondary"
                      />
                      <Typography className="pagesListFiltersTitleText">
                        Filters
                      </Typography>
                    </Button>
                  </Box>
                ) : null}
                {paginatedPages.length ? (
                  <SearchBar
                    pagesList={pagesList}
                    handleSearch={handleSearch}
                  />
                ) : null}
              </>
            ) : null}
          </Box>
        </Box>
      </>
      {paginatedPages.length ? (
        view === "table" ? (
          <>
            <TableContainer className="pagesTableContainer" component={Paper}>
              <Table className="pagesTable" stickyHeader>
                <TableHeader
                  sortConfig={sortConfig}
                  handleSortChange={handleSortChange}
                  isHeaderCheckboxChecked={isHeaderCheckboxChecked}
                  paginatedPages={paginatedPages}
                  setCheckedPages={setCheckedPages}
                  setIsHeaderCheckboxChecked={setIsHeaderCheckboxChecked}
                  anchorEl={anchorEl}
                  open={open}
                  handleClickCheckAllDropdown={handleClickCheckAllDropdown}
                  handleCloseCheckAllDropdown={handleCloseCheckAllDropdown}
                  handleCheckAllPages={handleCheckAllPages}
                  handleUncheckAllPages={handleUncheckAllPages}
                />
                <TableContent
                  pagesList={pagesList}
                  prevPagesList={prevPagesList}
                  paginatedPages={paginatedPages}
                  checkedPages={checkedPages}
                  selectedScanStatus={selectedScanStatus}
                  handleCheckboxChange={handleCheckboxChange}
                  handleOpenDeleteDialog={handleOpenDeleteDialog}
                />
              </Table>
            </TableContainer>
            {paginatedPages.length ? (
              <PaginationComponent
                page={page}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                count={totalPages}
                handlePageChange={handlePageChange}
              />
            ) : null}
          </>
        ) : (
          <TableContainer
            className="pagesTableContainer hierarchicalTableContainer"
            component={Paper}
          >
            <Table className="pagesTable" stickyHeader>
              <HierarchicalTableHeader />
              <HierarchicalTableContent
                sitemap={sitemap}
                prevSitemap={prevSitemap}
                checkedPages={checkedPages}
                setCheckedPages={setCheckedPages}
                handleOpenDeleteDialog={handleOpenDeleteDialog}
              />
            </Table>
          </TableContainer>
        )
      ) : (
        <Box className="pagesEmpty">
          <Box className="pagesEmptyIconContainer">
            <EmptyPagesIcon />
          </Box>
          {pagesList.length ? (
            <Typography className="pagesEmptyLabel">
              No pages match the selected filters
            </Typography>
          ) : (
            <>
              <Typography className="pagesEmptyLabel">No Pages Yet</Typography>
              <Typography className="pagesEmptyText" variant="body2">
                Website Mapping is nearly ready
              </Typography>
            </>
          )}
        </Box>
      )}
      <PagesFilters
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        selectedScanStatus={selectedScanStatus}
        setSelectedScanStatus={setSelectedScanStatus}
        allIssueTypes={allIssueTypes}
        selectedIssueTypes={selectedIssueTypes}
        setSelectedIssueTypes={setSelectedIssueTypes}
        selectedTestCasesStatus={selectedTestCasesStatus}
        setSelectedTestCasesStatus={setSelectedTestCasesStatus}
      />
      <DeletePagesConfirmationModal
        deleteOpen={deleteOpen}
        setDeleteOpen={setDeleteOpen}
        pagesToggler={pagesToggler}
        setPagesToggler={setPagesToggler}
        pagesToDelete={pagesToDelete}
        setPagesToDelete={setPagesToDelete}
        setCheckedPages={setCheckedPages}
      />
      <CreatePresetModal
        projectId={projectID}
        createPresetOpen={createPresetOpen}
        setCreatePresetOpen={setCreatePresetOpen}
        pagesToggler={pagesToggler}
        setPagesToggler={setPagesToggler}
        checkedPages={checkedPages}
        setCheckedPages={setCheckedPages}
      />
      <GenerateTestCasesConfirmationModal
        generateTestCasesForAllPagesOpen={generateTestCasesForAllPagesOpen}
        setGenerateTestCasesForAllPagesOpen={
          setGenerateTestCasesForAllPagesOpen
        }
        handleGenerateTestCases={handleGenerateTestCases}
        pagesToggler={pagesToggler}
        setPagesToggler={setPagesToggler}
      />
      <GenerateTestSettingsModal
        open={generateTestSettingsModalOpen}
        setOpen={setGenerateTestSettingsModalOpen}
        onRun={handleRunGenerateTest}
      />
      <EditPresetModal
        editPresetOpen={editPresetOpen}
        setEditPresetOpen={setEditPresetOpen}
        pagesToggler={pagesToggler}
        setPagesToggler={setPagesToggler}
        presetToEdit={presetToEdit}
      />
    </Box>
  );
};

export default PagesContent;
